'use client';

import { useState, useRef, useEffect } from 'react';
import { Send, Mic, MicOff } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/utils/cn';

interface ChatInputProps {
  onSendMessage: (message: string, voiceUsed?: boolean) => void;
  onStartVoice?: () => void;
  onStopVoice?: () => void;
  isRecording?: boolean;
  isLoading?: boolean;
  disabled?: boolean;
  placeholder?: string;
}

export function ChatInput({
  onSendMessage,
  onStartVoice,
  onStopVoice,
  isRecording = false,
  isLoading = false,
  disabled = false,
  placeholder = 'Scrivi un messaggio...',
}: ChatInputProps) {
  const [message, setMessage] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 150)}px`;
    }
  }, [message]);

  const handleSubmit = () => {
    if (message.trim() && !isLoading && !disabled) {
      onSendMessage(message.trim());
      setMessage('');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleVoiceToggle = () => {
    if (isRecording) {
      onStopVoice?.();
    } else {
      onStartVoice?.();
    }
  };

  return (
    <div className="border-t bg-white p-4">
      <div className="flex items-end gap-2">
        <div className="relative flex-1">
          <textarea
            ref={textareaRef}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={isRecording ? 'Registrazione in corso...' : placeholder}
            disabled={disabled || isLoading || isRecording}
            rows={1}
            className={cn(
              'w-full resize-none rounded-xl border border-gray-300 bg-white px-4 py-3 pr-12 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20',
              (disabled || isLoading) && 'cursor-not-allowed opacity-50',
              isRecording && 'border-red-300 bg-red-50'
            )}
          />

          {message.trim() && (
            <Button
              size="icon"
              variant="ghost"
              className="absolute bottom-1.5 right-1.5 h-8 w-8 rounded-lg"
              onClick={handleSubmit}
              disabled={isLoading || disabled}
            >
              <Send className="h-4 w-4" />
            </Button>
          )}
        </div>

        {(onStartVoice || onStopVoice) && (
          <Button
            size="icon"
            variant={isRecording ? 'destructive' : 'outline'}
            className={cn(
              'h-12 w-12 rounded-full',
              isRecording && 'animate-pulse'
            )}
            onClick={handleVoiceToggle}
            disabled={disabled || isLoading}
          >
            {isRecording ? (
              <MicOff className="h-5 w-5" />
            ) : (
              <Mic className="h-5 w-5" />
            )}
          </Button>
        )}
      </div>

      {isRecording && (
        <p className="mt-2 text-center text-sm text-red-500">
          Registrazione in corso... Clicca il microfono per fermare
        </p>
      )}
    </div>
  );
}
